import type { OrderStockStatus, OrderSupplierDraft } from '../api/orderStockStatusApi';
import type { OrderWarehouseSummary } from '../../../types/orderWarehouseSummary';
import type { WZDocumentDto } from '../../../dto/wz';
import type { OrderDto } from '../../../dto/order';
import type { TransportRouteDetailDto } from '../../../types/transport';
import { getTransportStatusDisplay } from '../../transports/transportRouteViews';
import type { OrderStepBarFacts } from './orderStepBar';

/**
 * Quantity facts the ZK card steps share (no money). One place reads the
 * backend read models; screens and the action bar only consume the result.
 * Unknown stays unknown: `null` when the reader has no source for it.
 */
export interface OrderProcessFacts {
  /** Every line issued by WZ. */
  allIssued: boolean;
  /** At least one WZ released for the order (or a line partly issued). */
  anyIssued: boolean;
  /** Lines the warehouse does not cover yet (stock-status `MISSING`). */
  missing: Array<{ orderProductId: string; name: string; quantity: number; unit: string }>;
  /** WZ documents that took goods off the warehouse (released / received), oldest first. */
  issuedWz: WZDocumentDto[];
}

const ISSUED_WZ_STATUSES = new Set(['RELEASED', 'RECEIVED']);

export function getOrderProcessFacts(
  stockStatus: OrderStockStatus | null,
  warehouse: OrderWarehouseSummary | null,
  wzDocuments: WZDocumentDto[] | null,
): OrderProcessFacts {
  const stockLines = stockStatus?.lines ?? [];
  const warehouseLines = warehouse?.lines ?? [];
  const allIssued = warehouseLines.length > 0
    ? warehouseLines.every((line) => line.remainingQuantity === 0)
    : stockLines.length > 0 && stockLines.every((line) => line.state === 'ISSUED');
  const issuedWz = (wzDocuments ?? []).filter((document) => ISSUED_WZ_STATUSES.has(document.status));
  const anyIssued = issuedWz.length > 0
    || warehouseLines.some((line) => (line.issuedQuantity ?? 0) > 0)
    || stockLines.some((line) => line.state === 'ISSUED');
  const missing = stockLines
    .filter((line) => line.state === 'MISSING' && (line.missing ?? 0) > 0)
    .map((line) => ({ orderProductId: line.orderProductId, name: line.name, quantity: line.missing ?? 0, unit: line.unit }));
  return { allIssued, anyIssued, missing, issuedWz };
}

/** Trucks linked to the ZK that are not cancelled (transport owner: OrderTransportsSection). */
export const activeOrderTransports = (transports: TransportRouteDetailDto[]) =>
  transports.filter((transport) => getTransportStatusDisplay(transport.status) !== 'cancelled');

/** Outgoing trucks of the ZK („Magazyn → klient”). */
export const outgoingOrderTransports = (transports: TransportRouteDetailDto[]) =>
  activeOrderTransports(transports).filter((transport) => transport.direction === 'OUTBOUND');

/**
 * The order-line and transport facts of the action bar (`OrderStepBarFacts`).
 * Unknown stays unknown: without sale prices nothing counts as missing a
 * price; without transport access the missing truck is `null`, and a
 * self-pickup never misses one.
 */
export function getOrderStepBarOrderFacts(input: {
  order: Pick<OrderDto, 'productsLoaded' | 'products' | 'deliveryMode' | 'fulfillmentSource' | 'hasAdvanceInvoice'>;
  canViewSalePrices: boolean;
  canViewTransports: boolean;
  transports: TransportRouteDetailDto[];
}): Pick<OrderStepBarFacts, 'linesWithoutPrice' | 'lineCount' | 'transportMissing' | 'hasAdvanceInvoice'> {
  const { order } = input;
  const products = order.productsLoaded ? order.products : [];
  return {
    linesWithoutPrice: input.canViewSalePrices
      ? products.filter((product) => !(Number(product.quantity) > 0) || !(Number(product.unitPrice) > 0)).length
      : 0,
    // Unloaded lines are not „no lines”.
    lineCount: order.productsLoaded ? products.length : 1,
    transportMissing: getOrderTransportMissing(order, input.canViewTransports, input.transports),
    hasAdvanceInvoice: order.hasAdvanceInvoice === true,
  };
}

/**
 * The ZK still needs an outgoing truck: self-pickup and „prosto z fabryki”
 * (the truck comes with the ZD) never do; unknown (`null`) without transport access.
 */
export function getOrderTransportMissing(
  order: Pick<OrderDto, 'deliveryMode' | 'fulfillmentSource'>,
  canViewTransports: boolean,
  transports: TransportRouteDetailDto[],
): boolean | null {
  if (order.deliveryMode === 'CUSTOMER_PICKUP' || order.fulfillmentSource === 'FACTORY_DIRECT') return false;
  return canViewTransports ? outgoingOrderTransports(transports).length === 0 : null;
}

/**
 * The ZD the ZK still counts on: a draft waiting for Logistyk or a reviewed
 * ZD that is not cancelled. A rejected draft or a cancelled ZD is no ZD — the
 * ZK card then speaks of the shortage and shows the old ZD only as history.
 */
export function activeSupplierDraft(stockStatus: OrderStockStatus | null): OrderSupplierDraft | null {
  const draft = stockStatus?.supplierDraft;
  if (!draft || draft.state === 'NONE' || draft.state === 'REJECTED') return null;
  if (draft.supplierOrderStatus === 'CANCELLED') return null;
  return draft;
}

/**
 * The ZD's status on the ZK card, as a translation key (orders namespace):
 * draft waiting for review, reviewed but not sent, its supplier-order status,
 * rejected or cancelled. `null` when there is no ZD at all.
 */
export function supplierDraftStatusKey(draft: OrderSupplierDraft | null | undefined): string | null {
  if (!draft || draft.state === 'NONE') return null;
  if (draft.state === 'REJECTED') return 'orders:process.summary.draftStatus.REJECTED';
  if (draft.supplierOrderStatus === 'CANCELLED') return 'orders:process.summary.draftStatus.CANCELLED';
  if (draft.state === 'PENDING') return 'orders:process.summary.draftStatus.PENDING';
  if (!draft.supplierOrderStatus || draft.supplierOrderStatus === 'DRAFT') return 'orders:process.summary.draftStatus.REVIEWED';
  return `orders:supplierOrders.status.${draft.supplierOrderStatus}`;
}

export interface OrderWaitingItem {
  key: string;
  params?: Record<string, string>;
}

/**
 * „Na co czeka zamówienie”: everything that really remains before the goods
 * can leave for the customer, in the order it happens — the ZD (to place, to
 * review, to send), the PZ of the missing goods, the outgoing truck. Empty
 * when nothing is missing. Unknown stays unknown: no truck item without
 * transport access (`transportMissing` null).
 */
export function getOrderWaitingItems(input: {
  stockStatus: OrderStockStatus | null;
  missing: OrderProcessFacts['missing'];
  transportMissing: boolean | null;
}): OrderWaitingItem[] {
  if (!input.missing.length) return [];
  const items: OrderWaitingItem[] = [];
  const draft = activeSupplierDraft(input.stockStatus);
  const number = draft?.supplierOrderNumber || '';
  if (!draft) items.push({ key: 'orders:process.fulfillment.waiting.placeZd' });
  else if (draft.state === 'PENDING') items.push({ key: 'orders:process.fulfillment.waiting.reviewZd', params: { number } });
  else if (!draft.supplierOrderStatus || draft.supplierOrderStatus === 'DRAFT') items.push({ key: 'orders:process.fulfillment.waiting.sendZd', params: { number } });
  items.push({ key: 'orders:process.fulfillment.waiting.receivePz' });
  if (input.transportMissing) items.push({ key: 'orders:process.fulfillment.waiting.planTransport' });
  return items;
}
